import { Box, Grid, IconButton, Typography, styled, useTheme } from "@mui/material";
import { GetSpotWalletInfo } from "store/api/wallet";
import { useEffect, useState } from "react";

import CardSkeleton from "components/Skeletons/Card.Skeleton";
import { DefaultUrlWeb } from "config/Configuration";
import NumberFormats from "components/NumberFormats";
import http from "config/http";
import useNotify from "hooks/useNotify";
import { useSearchParams } from "react-router-dom";
import ButtonAction from "../ButtonAction";
import { ChartPie, EyeIcon, EyeOffIcon, History } from "assets/Icon";
import HeaderWallet from "../HeaderWallet";
import ChartSpot from "../ChartSpot";
import LoadingSkeleton from "components/LoadingSkeleton";


export function SpotCard({ buttonItems, buttonOnChange }) {
  const notify = useNotify();
  const theme = useTheme();

  const [searchParams] = useSearchParams();
  const transfer = searchParams.get("transfer");

  const [spotWalletInfo, setSpotWalletInfo] = useState();
  const [showPrice, setShowPrice] = useState(true);

  const itemHeader = [
    {
      link: "/portfolio",
      icon: <ChartPie color={theme.palette.secondary.main} stroke={3} />,
    },
    {
      link: "/history/spot",
      icon: <History color={theme.palette.secondary.main} />,
    },
  ];


  const getSpotWalletInfo = async () => {
    try {
      const { data } = await http.get(GetSpotWalletInfo);
      setSpotWalletInfo(data.data);
    } catch (error) {
      notify(error?.response.data.message, "error");
    }
  };

  const handleSetShowPrice = () => {
    localStorage.setItem('show-spot-wallet', !showPrice)
    setShowPrice(!showPrice)
  }

  useEffect(() => {
    const currentShowPrice = localStorage.getItem('show-spot-wallet')
    if (currentShowPrice) {
      setShowPrice(JSON.parse(currentShowPrice));
    }

    getSpotWalletInfo();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfer]);

  return (
    <>
      {spotWalletInfo ? (
        <Box borderBottom={"1px solid"} borderColor={"modules.divider"} pb={4} width={"100%"} position={"relative"}>
          <Box pl={{ xs: 0, md: 4 }} width={"100%"}>
            <Grid container mt={2} >
              <BoxDate>
                <Typography component="small">
                  {spotWalletInfo?.date} <br /> {spotWalletInfo?.time}
                </Typography>
              </BoxDate>

              <Grid item md={6} xs={12} order={{ xs: 1, md: 1 }} display={{ xs: "none", md: "block" }}>
                <TypographyTitle
                  variant="h3"
                  color="common.white"
                  fontWeight={600}
                  marginBottom={4}
                >
                  Spot Wallet
                </TypographyTitle>
              </Grid>

              <Grid item md={6} xs={12} order={{ xs: 6, md: 2 }}>
                <ButtonAction
                  items={buttonItems}
                  onChange={buttonOnChange}
                  justifyContent={{ xs: "center", md: "end" }}
                />
              </Grid>

              <Grid item md={3} xs={8} order={{ xs: 1, md: 3 }} pl={{xs: 3, md: 0}}>
                <Box display={"flex"} alignItems={"center"} marginBottom={2.3} gap={1.5}>
                  <Typography
                    variant="h6"
                    color="common.white"
                    fontWeight={400}
                  >
                    Total Value Balance
                  </Typography>

                  <IconButton onClick={() => handleSetShowPrice()}>
                    {showPrice ? <EyeOffIcon color={theme.palette.text.primary} /> : <EyeIcon color={theme.palette.text.primary} />}
                  </IconButton>
                </Box>

                <BoxCoin>
                  <UsdtText variant="h1" color="common.white" fontWeight={600}>
                    {!showPrice ? <Typography component={"span"} variant="h1" fontFamily={"none !important"}>*******</Typography> : <NumberFormats value={spotWalletInfo?.totalBalanceUSDT} />}
                    <Typography component="small" fontWeight={500}>
                      USDT
                    </Typography>
                  </UsdtText>
                </BoxCoin>

                <TomanBox>
                  <Typography variant="h5" color="common.white" fontWeight={400}>
                    {!showPrice ? <Typography component={"span"} variant="h5" fontFamily={"none !important"}>*******</Typography> : <NumberFormats value={spotWalletInfo?.totalBalanceToman} />}
                    <Typography component="small" fontWeight={300}>
                      TOMAN
                    </Typography>
                  </Typography>
                </TomanBox>
              </Grid>

              <GridChart item md={6} order={{ md: 4 }}>
                <ChartSpot />
              </GridChart>

              <Grid item xs={4} md={3} order={{ xs: 2, md: 5 }}>
                <HeaderWallet itemHeader={itemHeader} my={0} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      ) : (
        <LoadingSkeleton sx={{ height: 304 }} />
      )
      }
    </>
  );
}

const BoxDate = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "120px",
  right: "6px",
  color: `${theme.palette.text.main}`,
  fontWeight: "400",
  fontSize: "13px",
  lineHeight: "18px",
  textAlign: "right",
  [theme.breakpoints.down('sm')]: {
    fontSize: "12px"
  },
  small: {
    color: theme.palette.common.white,
    fontWeight: "400",
    fontSize: "13px",
    lineHeight: "18px",
    textAlign: "end",
    [theme.breakpoints.down('sm')]: {
      fontSize: "12px"
    }
  },

  [theme.breakpoints.down('md')]: {
    top: "60px"
  }
}));

const BoxCoin = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1.3),
  img: {
    width: "30px",
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  small: {
    fontSize: "20px",
    marginLeft: theme.spacing(0.7),
    color: theme.palette.text.secondary
  },
}));

const TomanBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1.3),
  img: {
    width: "14px",
    margin: theme.spacing(0, 1, 0.5, 3.5)
  },
  small: {
    fontSize: "12px",
    marginLeft: theme.spacing(0.7),
    color: theme.palette.text.secondary
  },
}));

const GridChart = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const TypographyTitle = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const UsdtText = styled(Typography)(({ theme }) => ({
  fontSize: "40px",
  [theme.breakpoints.down('md')]: {
    fontSize: "28px"
  }
}));

export default SpotCard;
